/**
 * iPCLM-REQ-5 — Requisitions L2 Kit
 *
 * Mirrors `SourcingL2Kit.jsx` for the Requisitions module:
 *   - RQ_MODULES        : 8 sub-modules (RQ-5.1 → RQ-5.8) with icons, tone, label
 *   - RQ_AGENT_MAP      : per-sub-module AI agent persona (rq51 … rq58)
 *   - RQ_HOW_IT_WORKS   : ultra-detailed walkthrough sections per sub-module
 *   - RQ_INTEGRATIONS   : "Integration with other modules" cards per sub-module
 *   - RequisitionsQuickLinks  : 8-tile lateral nav across tabs
 *   - RequisitionsHowItWorks  : collapsible accordion
 *   - RequisitionsIntegrations: integration card grid
 */
import { useState } from 'react';
import {
  LayoutDashboard, MessageSquare, Sparkles, Wand2, ShieldCheck,
  Settings2, GitBranch, Inbox, Zap, BookOpen, ChevronDown, ChevronUp,
  ArrowRight, Link2,
} from 'lucide-react';

export const RQ_MODULES = [
  { code: 'RQ-5.1', key: 'dashboard',     label: 'P2P Dashboard',          Icon: LayoutDashboard, tone: 'indigo'   },
  { code: 'RQ-5.2', key: 'conversational',label: 'Conversational PR',      Icon: MessageSquare,   tone: 'violet'   },
  { code: 'RQ-5.3', key: 'nl-intake',     label: 'NL Intake',              Icon: Sparkles,        tone: 'fuchsia'  },
  { code: 'RQ-5.4', key: 'create',        label: 'Field Enrichment',       Icon: Wand2,           tone: 'amber'    },
  { code: 'RQ-5.5', key: 'detail',        label: 'Policy Compliance',      Icon: ShieldCheck,     tone: 'emerald'  },
  { code: 'RQ-5.6', key: 'matrix',        label: 'Approval Matrix',        Icon: Settings2,       tone: 'sky'      },
  { code: 'RQ-5.7', key: 'conditions',    label: 'Approval Workflow',      Icon: GitBranch,       tone: 'rose'     },
  { code: 'RQ-5.8', key: 'approvals',     label: 'Approver Experience',    Icon: Inbox,           tone: 'teal'     },
];

/** Map tab-key → AI agent persona. */
export const RQ_AGENT_MAP = {
  dashboard:      { module: 'rq51', name: 'P2P Dashboard Curator Agent' },
  conversational: { module: 'rq52', name: 'Conversational PR Co-Pilot Agent' },
  'nl-intake':    { module: 'rq53', name: 'NL Intake Parser Agent' },
  create:         { module: 'rq54', name: 'Enrichment Forensic Agent' },
  detail:         { module: 'rq55', name: 'Policy Compliance Sentinel Agent' },
  matrix:         { module: 'rq56', name: 'Approval Matrix Architect Agent' },
  conditions:     { module: 'rq57', name: 'Approval Workflow Conductor Agent' },
  approvals:      { module: 'rq58', name: 'Approver Experience Concierge Agent' },
};

/** Ultra-detailed How-it-works per tab. */
export const RQ_HOW_IT_WORKS = {
  dashboard: [
    { title: 'Live P2P pipeline visibility',
      body: 'Six KPI tiles drive the dashboard — Total PRs, Total Committed Value (₹), Pending Approvals, AVL Compliance %, Policy Violation %, and Rejection Rate. The pipeline grid below is filterable by status and links to the per-PR detail tab.',
      steps: ['KPI tiles re-compute from `purchaseRequisitions` via the `/p2p/requisitions/dashboard` aggregate.',
              'Status filter is server-side (status query param).',
              'Open any row → loads the Policy + Approval Chain panel in the Detail tab.'] },
    { title: 'AI-augmented insights',
      body: 'The dashboard agent surfaces (a) the highest-value PR stuck for the longest, (b) the slowest moving approver this week, and (c) the top 3 categories driving demand spikes.',
      steps: ['Daily PR volume sparkline lives in the Analytics tab.',
              'Stuck-PR detection uses `submittedAt` vs `slaDays` per chain level.'] },
  ],
  conversational: [
    { title: 'Cognara tool_use loop',
      body: 'The agent invokes three tools — searchCatalogue (embedding match), getAVLSuppliers (AVL × performance ranker), and createPR (single-shot persist). The agent decides which tools to call from the latest user message.',
      steps: ['POST /api/requisitions/conversational/start → returns sessionId + welcome.',
              'POST /api/requisitions/conversational/{sid}/tool-message → returns reply + toolsUsed[] + toolOutputs + createdPR.',
              'When the user says "create the PR" the createPR tool persists and the PR number is returned inline.'] },
    { title: 'Confidence-first answers',
      body: 'Every tool response carries a similarity / score field. The agent quotes those scores so users can override any field.',
    },
  ],
  'nl-intake': [
    { title: 'Single-field to fully-structured PR',
      body: 'Drop a sentence — Cognara extracts entities (item, qty, UoM, plant, RBD, urgency), embedding-maps to category, ranks AVL suppliers, and estimates price using the spend-cube 90-day mean. The draft can be tweaked before confirming.',
      steps: ['POST /api/requisitions/nl-intake { text } → draft with per-field confidence chips.',
              'POST /api/requisitions { …draft } persists the PR.',
              'No tool-use here — single-shot extraction.'] },
  ],
  create: [
    { title: 'Auto-enrichment after persist',
      body: 'When a manual PR is saved, RQ-5.4 enrichment fires server-side — categoryId, recommendedSupplier, AVL status, GL account, and CAPEX/OPEX hint are filled with sources and confidence.',
      steps: ['Hit /api/requisitions/{prNumber}/enrich to re-run for an existing PR.',
              'Confidence > 85 % = green chip, 60–85 % amber, else rose.',
              'Source tags: AI_SUGGESTED · CATALOGUE · SPEND_HISTORY · CONTRACT · HEURISTIC.'] },
  ],
  detail: [
    { title: 'Seven-check policy engine',
      body: '(1) Budget availability vs `budgets` collection · (2) AVL enforcement on every line · (3) Contract coverage check · (4) Split-purchase ≤30-day window · (5) Separation of Duties · (6) Competitive threshold (sourcing event needed > limit) · (7) Catalogue compliance for catalogue-eligible items.',
      steps: ['POST /api/requisitions/{n}/policy-check re-runs all 7 checks.',
              'Overall = WORST(checks). VIOLATIONS block submit unless overridden.',
              'POST /api/requisitions/{n}/policy-override records compliance-officer override.'] },
    { title: 'Routing decisions',
      body: 'Approved PRs can be converted to a PO directly, routed to a sourcing event (when above competitive threshold), to a Framework call-off (when a framework matches), or to a Direct Award (with justification).',
    },
  ],
  matrix: [
    { title: 'Value-band × category × department rules',
      body: 'Each rule has a priority (lower wins), conditions (valueMin, valueMax, categoryId, department, capexOpex), and an approval chain (level → approverType → slaDays → optional parallel). Empty chain = auto-approve.',
      steps: ['GET /api/admin/approval-matrix returns rules in priority order.',
              'POST / PATCH / DELETE for CRUD; DELETE soft-disables (isActive=false).',
              'POST /api/admin/approval-matrix/simulate runs a what-if for any value.'] },
    { title: 'Simulator before commit',
      body: 'Always simulate before saving — confirms the matched rule, the resolved chain, and whether the PR will be auto-approved.',
    },
  ],
  conditions: [
    { title: 'Conditional approvals — track and resolve',
      body: 'When an approver picks "Approve with conditions" on the final level, the PR enters CONDITIONALLY_APPROVED. The buyer/coordinator owns resolving every attached condition before the PR transitions to APPROVED and becomes routable.',
      steps: ['List comes from /api/requisitions?status=CONDITIONALLY_APPROVED.',
              'Conditions live on approvalChain[].conditions[] (per level).',
              'PATCH /api/approvals/{n}/conditions/resolve → APPROVED + audit trail.'] },
  ],
  approvals: [
    { title: 'Approver-first queue',
      body: 'Desktop = a tabular queue with batch-approve, one-click email links, and per-row Approve/Reject. Mobile = swipe-left to reject, swipe-right to approve cards (with 120 px threshold and a visual approve/reject hint).',
      steps: ['GET /api/approvals/pending lists pending PRs for the current approver.',
              'POST /api/approvals/{n}/one-click-token mints a 24-h token, deep-linkable from email.',
              'POST /api/approvals/batch-approve auto-approves any selected PR below the threshold whose policy is COMPLIANT.'] },
    { title: 'SLA reminders + escalations',
      body: 'The 15-minute SLA cron (`rq5_sla_tick`) sends a reminder on SLA-1d and escalates on SLA breach. Notifications are emitted to communicationLog (Resend/Twilio adapters pluggable).',
    },
  ],
};

/** Cross-module integration cards per sub-module. */
export const RQ_INTEGRATIONS = {
  dashboard: [
    { code: 'SCM-2', label: 'Spend Cube',         href: '/app/scm/spend-cube',   note: 'Committed PR value rolls into committed-spend column.' },
    { code: 'CON-2', label: 'Contracts',          href: '/app/contracts',        note: 'Contract coverage % feeds the AVL chip.' },
    { code: 'P2P',   label: 'Purchase Orders',    href: '/app/p2p',              note: 'Converted PRs surface as POs.' },
  ],
  conversational: [
    { code: 'SCM-1', label: 'Taxonomy',           href: '/app/scm/taxonomy',     note: 'Category mapping uses live taxonomy anchors + embeddings.' },
    { code: 'SRM-2', label: 'Qualification',      href: '/app/srm/qualification',note: 'AVL supplier ranking respects qualification status.' },
    { code: 'CAT',   label: 'Catalogue',          href: '/app/scm/catalogue',    note: 'searchCatalogue tool reads the live catalogue.' },
  ],
  'nl-intake': [
    { code: 'SCM-1', label: 'Taxonomy',           href: '/app/scm/taxonomy',     note: 'Embedding category mapping pulls anchors from taxonomy.' },
    { code: 'SCM-2', label: 'Spend Cube',         href: '/app/scm/spend-cube',   note: 'Price estimate uses 90-day mean from spend history.' },
    { code: 'SRM-3', label: 'Performance',        href: '/app/srm/performance',  note: 'AVL ranker uses performance scorecard signal.' },
  ],
  create: [
    { code: 'CAT',   label: 'Catalogue',          href: '/app/scm/catalogue',    note: 'Catalogue matches preference enrichment.' },
    { code: 'SRM-2', label: 'Qualification',      href: '/app/srm/qualification',note: 'AVL status drives avlStatus field.' },
    { code: 'CON-2', label: 'Contracts',          href: '/app/contracts',        note: 'Contract refs auto-attached for in-contract spend.' },
  ],
  detail: [
    { code: 'CON-2', label: 'Contracts',          href: '/app/contracts',        note: 'Coverage check #3 reads active contracts.' },
    { code: 'SS-4',  label: 'Sourcing',           href: '/app/sourcing/lifecycle', note: 'Competitive threshold breach → auto-create draft event.' },
    { code: 'FRAM',  label: 'Frameworks',         href: '/app/sourcing/framework-agreements', note: 'Framework match → call-off route.' },
    { code: 'BDG',   label: 'Budgets',            href: '/app/admin/system-config', note: 'Budget availability check #1.' },
  ],
  matrix: [
    { code: 'DOA',   label: 'DOA Matrix',         href: '/app/sourcing/doa-matrix', note: 'Reuses delegation-of-authority taxonomy.' },
    { code: 'AUTH',  label: 'Authority Matrix',   href: '/app/admin/authority-matrix', note: 'Approver-type roles resolve to live users.' },
  ],
  conditions: [
    { code: 'NOTIF', label: 'Notifications',      href: '/app/admin/integrations', note: 'Reminder + escalation events emitted on resolve.' },
    { code: 'CON-2', label: 'Contracts',          href: '/app/contracts',        note: 'Condition fulfilment may require contract amendment.' },
  ],
  approvals: [
    { code: 'CMS',   label: 'Communication',      href: '/app/sourcing/communication-engine', note: 'One-click email + SLA reminders dispatched via template engine.' },
    { code: 'AUTH',  label: 'Authority Matrix',   href: '/app/admin/authority-matrix', note: 'Delegation routes resolve via live authority data.' },
  ],
};

/* ─────────── Quick Links — 8-tile lateral nav across tabs ─────────── */
/* DEPRECATED iter-127: icon-tile QL row replaced by the canonical green
   QuickLinksPanel (Internal + External) auto-rendered via ModulePageShell. */
export function RequisitionsQuickLinks() {
  return null;
}

/* DEPRECATED iter-127: HowItWorks chip removed (duplicate of Quick Links + ⌘K). */
/* RESTORED iter-132: per-tab ultra-detailed walkthrough accordion. */
export function RequisitionsHowItWorks({ moduleKey, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const sections = RQ_HOW_IT_WORKS[moduleKey] || [];
  if (!sections.length) return null;
  return (
    <div className="rounded-xl border border-sky-200 bg-sky-50/30 mb-3"
         data-testid={`rq-how-${moduleKey}`}>
      <button onClick={() => setOpen(!open)}
              className="w-full flex items-center justify-between p-2.5 text-left"
              data-testid={`rq-how-toggle-${moduleKey}`}>
        <div className="flex items-center gap-2 text-xs font-semibold text-sky-900">
          <BookOpen className="w-3.5 h-3.5"/> How it works — ultra-detailed walkthrough
        </div>
        {open ? <ChevronUp className="w-3.5 h-3.5 text-sky-700"/> : <ChevronDown className="w-3.5 h-3.5 text-sky-700"/>}
      </button>
      {open && (
        <div className="p-2.5 pt-0 space-y-2 text-[11.5px] text-sky-900/85">
          {sections.map((s, i) => (
            <div key={i} className="rounded bg-white border border-sky-100 p-2.5"
                 data-testid={`rq-how-section-${moduleKey}-${i}`}>
              <div className="font-semibold text-[12px] text-sky-950 mb-1 flex items-center gap-1.5">
                <span className="bg-sky-100 text-sky-700 rounded px-1.5 py-0.5 text-[10px] font-mono">{i+1}</span>
                {s.title}
              </div>
              <div className="text-[11px] leading-relaxed">{s.body}</div>
              {s.steps && (
                <ol className="list-decimal list-inside mt-1.5 text-[11px] space-y-0.5 text-sky-800/90">
                  {s.steps.map((step, j) => <li key={j}>{step}</li>)}
                </ol>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─────────── Integration cards ─────────── */
export function RequisitionsIntegrations({ moduleKey }) {
  const items = RQ_INTEGRATIONS[moduleKey] || [];
  if (!items.length) return null;
  return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50/30 p-3 mb-4"
         data-testid={`rq-integrations-${moduleKey}`}>
      <div className="text-[11px] font-semibold text-emerald-900 uppercase mb-2 flex items-center gap-1">
        <Link2 className="w-3 h-3"/> Integration with other modules
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
        {items.map((i, k) => (
          <a key={k} href={i.href}
             className="block rounded-lg bg-white border border-emerald-100 p-2 hover:border-emerald-300 hover:shadow-sm transition"
             data-testid={`rq-integration-${moduleKey}-${i.code}`}>
            <div className="flex items-center gap-2 mb-0.5">
              <span className="font-mono text-[10px] bg-emerald-100 text-emerald-700 rounded px-1.5 py-0.5">{i.code}</span>
              <span className="text-xs font-semibold text-emerald-900">{i.label}</span>
              <ArrowRight className="w-3 h-3 text-emerald-700 ml-auto"/>
            </div>
            <div className="text-[11px] text-emerald-800/80 leading-snug">{i.note}</div>
          </a>
        ))}
      </div>
    </div>
  );
}
